import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { AuthContext } from './AuthContext.jsx';
import { useOrganizationSettings } from './OrganizationSettingsContext';
import { getCompaniesApi } from '../api/api';

const CompanyContext = createContext();

// Helper function to format company object
const formatCompany = (company) => ({
  value: company.companyId,
  label: `${company.companyName} (${company.companyId})`
});

// Helper function to get stored company from localStorage
const getStoredCompany = () => {
  try {
    const stored = localStorage.getItem('selectedCompany');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

// Helper function to check if company exists in list
const companyExists = (companyId, companies) => {
  return companies.some(c => String(c.companyId) === String(companyId));
};

export function CompanyProvider({ children }) {
  const { user } = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const [selectedCompany, setSelectedCompany] = useState(getStoredCompany);
  const [companyList, setCompanyList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const isUserSelectionRef = useRef(false);

  // Fetch companies based on user assignments
  useEffect(() => {
    // Clear everything if userWise is disabled
    if (!user?.tallyUserId) {
      setCompanyList([]);
      setSelectedCompany(null);
      localStorage.removeItem('selectedCompany');
      isUserSelectionRef.current = false;
      setIsReady(true);
      return;
    }
    
    setLoading(true);
    getCompaniesApi({ tallyUserId: user.tallyUserId })
      .then(res => {
        const companies = res.data?.data || res.data || [];
        
        if (!Array.isArray(companies)) {
          setCompanyList([]);
          return;
        }
        
        setCompanyList(companies);
        
        // If no companies, clear selection
        if (companies.length === 0) {
          setSelectedCompany(null);
          localStorage.removeItem('selectedCompany');
          return;
        }
        
        // If user hasn't manually selected, set initial selection
        if (!isUserSelectionRef.current) {
          const storedCompany = getStoredCompany();
          
          // Use stored company if it exists in the list, otherwise use first company
          const companyToSelect = storedCompany && companyExists(storedCompany.value, companies)
            ? companies.find(c => String(c.companyId) === String(storedCompany.value))
            : companies[0];
          
          const formattedCompany = formatCompany(companyToSelect);
          setSelectedCompany(formattedCompany);
          localStorage.setItem('selectedCompany', JSON.stringify(formattedCompany));
          // Dispatch custom event to notify settings context of company change
          window.dispatchEvent(new Event('companyChanged'));
        } else {
          // User has manually selected - verify it still exists
          setSelectedCompany(prev => {
            if (prev?.value && companyExists(prev.value, companies)) {
              return prev; // Keep current selection
            }
            
            // Selection is invalid, reset to first company
            isUserSelectionRef.current = false;
            const formattedCompany = formatCompany(companies[0]);
            localStorage.setItem('selectedCompany', JSON.stringify(formattedCompany));
            return formattedCompany;
          });
        }
      })
      .catch((error) => {
        console.error('Error fetching companies:', error);
        setCompanyList([]);
        setSelectedCompany(null);
      })
      .finally(() => {
        setLoading(false);
        setIsReady(true);
      });
  }, [settings?.userWise, user?.tallyUserId]);

  const updateSelectedCompany = (company) => {
    if (!company?.value) {
      return; // Don't clear selection - preserve current value
    }
    
    isUserSelectionRef.current = true;
    const formattedCompany = {
      value: company.value,
      label: company.label || `${company.value}`
    };
    
    setSelectedCompany(formattedCompany);
    localStorage.setItem('selectedCompany', JSON.stringify(formattedCompany));
    // Dispatch custom event to notify settings context of company change
    window.dispatchEvent(new Event('companyChanged'));
  };

  return (
    <CompanyContext.Provider
      value={{
        selectedCompany,
        companyList,
        loading,
        isReady, // Expose ready state
        updateSelectedCompany,
        companyId: selectedCompany?.value || null,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
}

